import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Flex from '@react-css/flex'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import styled from 'styled-components'

import { fetchFloors } from '../../../../api/buildingFetches'
import {
  listTrackerJobTypes,
  ProjectJobType,
} from '../../../../api/jobs'
import { listProjects } from '../../../../api/projects'
import {
  listProjectScheduleActivities,
  ProjectScheduleActivity,
  replaceProjectScheduleActivities,
} from '../../../../api/scheduleActivities'
import {
  listProjectTrackers,
  ProjectTracker,
} from '../../../../api/trackers'
import { Project, ProjectFloor } from '../../../../api/types'
import { useNotifications } from '../../../../contexts/notificationProvider'
import { useUserContext } from '../../../../contexts/userContext'
import { PATH_STRINGS } from '../../../../hooks/useGeneratedPaths'
import {
  ScheduleImportSession,
  ScheduleTaskMapping,
  XerTask,
} from '../../../../utils/xer'
import { AddProjectButton } from '../../buildings_page/components/AddProjectButton'

import { buildScheduleUpdateSummary } from './scheduleImportDiff'
import {
  getScheduleImportSession,
  setScheduleImportMappings,
  setScheduleImportProject,
} from './scheduleImportSession'

const TASK_TYPE_LABELS: Record<string, string> = {
  TT_Task: 'Task',
  TT_Mile: 'Milestone',
  TT_FinMile: 'Finish Milestone',
}

const STATUS_LABELS: Record<string, string> = {
  TK_Complete: 'Complete',
  TK_NotStart: 'Not Started',
  TK_Active: 'Active',
}

type ProjectOption = { value: string; label: string }
type FloorOption = { value: string; label: string }
type TrackerOption = { value: number; label: string }
type JobTypeOption = { value: number; label: string }

function formatXerDate(value: string | null): string {
  if (!value) {
    return '—'
  }
  return value.slice(0, 10)
}

function taskTypeLabel(taskType: string): string {
  return TASK_TYPE_LABELS[taskType] ?? taskType
}

function statusLabel(statusCode: string): string {
  return STATUS_LABELS[statusCode] ?? statusCode
}

function mappingLabel(mapping: ScheduleTaskMapping): string {
  const floorPart = mapping.floorName || mapping.floorCode
  const workPart = mapping.jobTypeName
    ? `${mapping.trackerName} > ${mapping.jobTypeName}`
    : mapping.trackerName
  if (floorPart) {
    return `${floorPart} > ${workPart}`
  }
  return workPart
}

export const ScheduleImportPage = () => {
  const { state: userState } = useUserContext()
  const { addNotification } = useNotifications()

  const [session, setSession] = useState<ScheduleImportSession | null>(() =>
    getScheduleImportSession(),
  )

  const reloadSession = useCallback(() => {
    setSession(getScheduleImportSession())
  }, [])

  const [search, setSearch] = useState('')
  const [unmappedOnly, setUnmappedOnly] = useState(false)
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [assignFloorCode, setAssignFloorCode] = useState<string | null>(null)
  const [assignTrackerId, setAssignTrackerId] = useState<number | null>(null)
  const [assignJobTypeId, setAssignJobTypeId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadingMappings, setLoadingMappings] = useState(false)
  const [savedActivities, setSavedActivities] = useState<
    ProjectScheduleActivity[]
  >([])
  const loadedMappingsKeyRef = useRef<string | null>(null)

  const { data: projects, isLoading: projectsLoading } = useQuery(
    ['schedule-import-projects', userState.public_id],
    () => listProjects(userState.public_id),
    { enabled: !!userState.public_id },
  )

  const projectPublicId = session?.projectPublicId ?? null

  const { data: trackersResponse, isLoading: trackersLoading } = useQuery(
    ['schedule-import-trackers', projectPublicId],
    () => listProjectTrackers(projectPublicId as string),
    { enabled: !!projectPublicId },
  )

  const { data: floors = [], isLoading: floorsLoading } = useQuery(
    ['schedule-import-floors', projectPublicId],
    () => fetchFloors(projectPublicId as string),
    { enabled: !!projectPublicId },
  )

  const trackers = useMemo(
    () => trackersResponse?.data ?? [],
    [trackersResponse?.data],
  )

  const floorList = useMemo(
    () => (Array.isArray(floors) ? (floors as ProjectFloor[]) : []),
    [floors],
  )

  const selectedTracker = useMemo(
    () => trackers.find((t: ProjectTracker) => t.id === assignTrackerId) ?? null,
    [trackers, assignTrackerId],
  )
  const selectedTrackerName = selectedTracker?.type.name ?? null

  const { data: jobTypesResponse, isLoading: jobTypesLoading } = useQuery(
    ['schedule-import-job-types', projectPublicId, selectedTrackerName],
    () =>
      listTrackerJobTypes(
        projectPublicId as string,
        selectedTrackerName as string,
      ),
    { enabled: !!projectPublicId && !!selectedTrackerName },
  )

  const jobTypes = useMemo(() => {
    const raw = (jobTypesResponse as { data?: ProjectJobType[] } | undefined)
      ?.data
    const list = Array.isArray(raw) ? raw : []
    return list.filter(
      (pjt: ProjectJobType) => pjt.is_active !== false && pjt.id != null,
    )
  }, [jobTypesResponse])

  const mappingsByTaskId = useMemo(() => {
    const map = new Map<string, ScheduleTaskMapping>()
    for (const mapping of session?.mappings ?? []) {
      map.set(mapping.taskId, mapping)
    }
    return map
  }, [session?.mappings])

  const filteredTasks = useMemo((): XerTask[] => {
    if (!session) {
      return []
    }
    const q = search.trim().toLowerCase()
    return session.schedule.tasks.filter((task: XerTask) => {
      if (unmappedOnly && mappingsByTaskId.has(task.taskId)) {
        return false
      }
      if (!q) {
        return true
      }
      return (
        task.taskCode.toLowerCase().includes(q) ||
        task.taskName.toLowerCase().includes(q) ||
        task.wbsPath.toLowerCase().includes(q)
      )
    })
  }, [session, search, unmappedOnly, mappingsByTaskId])

  const projectOptions: ProjectOption[] = useMemo(
    () =>
      (projects ?? []).map((p: Project) => ({
        value: p.public_id,
        label: p.name,
      })),
    [projects],
  )

  const selectedProjectOption =
    projectOptions.find(o => o.value === projectPublicId) ?? null

  const floorOptions: FloorOption[] = useMemo(
    () =>
      floorList.map((f: ProjectFloor) => ({
        value: f.floor_code,
        label: f.name ? `${f.name} (${f.floor_code})` : f.floor_code,
      })),
    [floorList],
  )

  const selectedFloorOption =
    floorOptions.find(o => o.value === assignFloorCode) ?? null

  const trackerOptions: TrackerOption[] = useMemo(
    () =>
      trackers.map((t: ProjectTracker) => ({
        value: t.id,
        label: t.type.name,
      })),
    [trackers],
  )

  const selectedTrackerOption =
    trackerOptions.find(o => o.value === assignTrackerId) ?? null

  const jobTypeOptions: JobTypeOption[] = useMemo(
    () =>
      jobTypes.map((pjt: ProjectJobType) => ({
        value: pjt.id as number,
        label: pjt.job_type.name,
      })),
    [jobTypes],
  )

  const selectedJobTypeOption =
    jobTypeOptions.find(o => o.value === assignJobTypeId) ?? null

  const mappingsByTrackerJob = useMemo(() => {
    const grouped = new Map<
      string,
      {
        floorCode: string
        trackerId: number
        trackerName: string
        jobTypeId: number
        jobTypeName: string
        label: string
        tasks: ScheduleTaskMapping[]
      }
    >()
    for (const mapping of session?.mappings ?? []) {
      const key = `${mapping.floorCode}:${mapping.trackerId}:${
        mapping.jobTypeId ?? 'none'
      }`
      const existing = grouped.get(key)
      if (existing) {
        existing.tasks.push(mapping)
      } else {
        grouped.set(key, {
          floorCode: mapping.floorCode,
          trackerId: mapping.trackerId,
          trackerName: mapping.trackerName,
          jobTypeId: mapping.jobTypeId,
          jobTypeName: mapping.jobTypeName,
          label: mappingLabel(mapping),
          tasks: [mapping],
        })
      }
    }
    return Array.from(grouped.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    )
  }, [session?.mappings])

  const updateSummary = useMemo(() => {
    if (!session || !projectPublicId) {
      return null
    }
    return buildScheduleUpdateSummary({
      tasks: session.schedule.tasks,
      mappedTaskIds: new Set(session.mappings.map(m => m.taskId)),
      savedActivities,
    })
  }, [session, projectPublicId, savedActivities])

  useEffect(() => {
    setSelectedTaskIds(new Set())
    setAssignFloorCode(null)
    setAssignTrackerId(null)
    setAssignJobTypeId(null)
  }, [projectPublicId])

  useEffect(() => {
    setAssignJobTypeId(null)
  }, [assignTrackerId])

  // Recall saved mappings once per project + uploaded file (carry floor/job by task id)
  useEffect(() => {
    if (!session || !projectPublicId || trackersLoading || floorsLoading) {
      return
    }
    const loadKey = `${projectPublicId}:${session.fileName}`
    if (loadedMappingsKeyRef.current === loadKey) {
      return
    }

    let cancelled = false
    setLoadingMappings(true)
    listProjectScheduleActivities(projectPublicId)
      .then(res => {
        if (cancelled) {
          return
        }
        const activities = res.data ?? []
        setSavedActivities(activities)
        const tasksById = new Map(
          session.schedule.tasks.map((t: XerTask) => [t.taskId, t]),
        )
        const trackersByName = new Map(
          trackers.map((t: ProjectTracker) => [t.type.name, t]),
        )
        const floorsByCode = new Map(
          floorList.map((f: ProjectFloor) => [f.floor_code, f]),
        )

        // Start from any in-session mappings for tasks still in this file
        const nextByTaskId = new Map<string, ScheduleTaskMapping>()
        for (const mapping of session.mappings) {
          if (tasksById.has(mapping.taskId)) {
            nextByTaskId.set(mapping.taskId, mapping)
          }
        }

        // Overlay saved DB mappings for matching external_task_ids
        activities.forEach((activity: ProjectScheduleActivity) => {
          const task = tasksById.get(activity.external_task_id)
          if (!task) {
            return
          }
          const trackerName = activity.tracker_name
          if (
            !trackerName ||
            activity.project_job_type_id == null ||
            !activity.floor_code
          ) {
            return
          }
          const tracker = trackersByName.get(trackerName)
          if (!tracker) {
            return
          }
          const floor = floorsByCode.get(activity.floor_code)
          nextByTaskId.set(activity.external_task_id, {
            taskId: activity.external_task_id,
            taskCode: task.taskCode || activity.task_code,
            floorCode: activity.floor_code,
            floorName: floor?.name || activity.floor_code,
            trackerId: tracker.id,
            trackerName,
            jobTypeId: activity.project_job_type_id,
            jobTypeName: activity.job_type_name || '',
          })
        })

        loadedMappingsKeyRef.current = loadKey
        setScheduleImportMappings(Array.from(nextByTaskId.values()))
        reloadSession()
      })
      .catch(() => {
        if (!cancelled) {
          setSavedActivities([])
          addNotification('Could not load saved schedule mappings.', 'error')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingMappings(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [
    projectPublicId,
    trackersLoading,
    floorsLoading,
    trackers,
    floorList,
    session,
    reloadSession,
    addNotification,
  ])

  useEffect(() => {
    if (!projectPublicId) {
      loadedMappingsKeyRef.current = null
      setSavedActivities([])
    }
  }, [projectPublicId])

  const allFilteredSelected =
    filteredTasks.length > 0 &&
    filteredTasks.every((t: XerTask) => selectedTaskIds.has(t.taskId))

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedTaskIds(prev => {
        const next = new Set(prev)
        filteredTasks.forEach((t: XerTask) => {
          next.delete(t.taskId)
        })
        return next
      })
    } else {
      setSelectedTaskIds(prev => {
        const next = new Set(prev)
        filteredTasks.forEach((t: XerTask) => {
          next.add(t.taskId)
        })
        return next
      })
    }
  }

  const toggleTask = (taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev)
      if (next.has(taskId)) {
        next.delete(taskId)
      } else {
        next.add(taskId)
      }
      return next
    })
  }

  const onProjectChange = (option: ProjectOption | null) => {
    setScheduleImportProject(option?.value ?? null)
    reloadSession()
  }

  const assignSelected = () => {
    if (
      !session ||
      !assignFloorCode ||
      assignTrackerId == null ||
      assignJobTypeId == null ||
      selectedTaskIds.size === 0
    ) {
      return
    }
    const floor = floorList.find(
      (f: ProjectFloor) => f.floor_code === assignFloorCode,
    )
    const tracker = trackers.find(
      (t: ProjectTracker) => t.id === assignTrackerId,
    )
    const jobType = jobTypes.find(
      (pjt: ProjectJobType) => pjt.id === assignJobTypeId,
    )
    if (!floor || !tracker || !jobType || jobType.id == null) {
      return
    }

    const tasksById = new Map<string, XerTask>()
    session.schedule.tasks.forEach((t: XerTask) => {
      tasksById.set(t.taskId, t)
    })
    const nextMappings: ScheduleTaskMapping[] = [...session.mappings]
    const indexByTaskId = new Map<string, number>()
    nextMappings.forEach((m, i) => {
      indexByTaskId.set(m.taskId, i)
    })

    const selectedIds = Array.from(selectedTaskIds)
    const label = `${floor.name || floor.floor_code} > ${tracker.type.name} > ${
      jobType.job_type.name
    }`
    selectedIds.forEach(taskId => {
      const task = tasksById.get(taskId)
      if (!task) {
        return
      }
      const mapping: ScheduleTaskMapping = {
        taskId: task.taskId,
        taskCode: task.taskCode,
        floorCode: floor.floor_code,
        floorName: floor.name || floor.floor_code,
        trackerId: tracker.id,
        trackerName: tracker.type.name,
        jobTypeId: jobType.id as number,
        jobTypeName: jobType.job_type.name,
      }
      const existingIndex = indexByTaskId.get(taskId)
      if (existingIndex != null) {
        nextMappings[existingIndex] = mapping
      } else {
        indexByTaskId.set(taskId, nextMappings.length)
        nextMappings.push(mapping)
      }
    })

    setScheduleImportMappings(nextMappings)
    reloadSession()
    setSelectedTaskIds(new Set())
    addNotification(
      `Assigned ${selectedIds.length} task(s) to ${label}`,
      'success',
    )
  }

  const clearAllMappings = () => {
    if (!session || session.mappings.length === 0) {
      return
    }
    setScheduleImportMappings([])
    reloadSession()
    setSelectedTaskIds(new Set())
    addNotification('Cleared all mappings', 'success')
  }

  const removeMapping = (taskId: string) => {
    if (!session) {
      return
    }
    const nextMappings = session.mappings.filter(
      (m: ScheduleTaskMapping) => m.taskId !== taskId,
    )
    setScheduleImportMappings(nextMappings)
    reloadSession()
  }

  const onSaveAll = async () => {
    if (!session || !projectPublicId) {
      addNotification('Select a project before saving.', 'warning')
      return
    }

    const tasksById = new Map(
      session.schedule.tasks.map((t: XerTask) => [t.taskId, t]),
    )
    const activities = session.mappings
      .map(mapping => {
        const task = tasksById.get(mapping.taskId)
        if (!task) {
          return null
        }
        return {
          external_task_id: task.taskId,
          external_guid: task.guid || null,
          task_code: task.taskCode,
          task_name: task.taskName,
          task_type: task.taskType,
          status_code: task.statusCode,
          wbs_path: task.wbsPath || null,
          floor_code: mapping.floorCode,
          project_job_type_id: mapping.jobTypeId,
          dates: task.dates,
        }
      })
      .filter((item): item is NonNullable<typeof item> => item != null)

    setSaving(true)
    try {
      const result = await replaceProjectScheduleActivities(projectPublicId, {
        source_file_name: session.fileName,
        activities,
      })
      setSavedActivities(result.data ?? [])
      const orphanCount = (result.data ?? []).filter(
        activity => !tasksById.has(activity.external_task_id),
      ).length
      const orphanNote =
        orphanCount > 0
          ? ` ${orphanCount} previously mapped task(s) not in this file were kept.`
          : ''
      addNotification(
        `Saved ${activities.length} schedule mapping(s).${orphanNote}`,
        'success',
      )
    } catch {
      addNotification('Failed to save schedule mappings.', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!session) {
    return (
      <Page>
        <EmptyState>
          <h2>No schedule loaded</h2>
          <p>
            Upload a Primavera <code>.xer</code> file from the Integrations page
            to get started.
          </p>
          <Link to={PATH_STRINGS.userProfileIntegrations}>
            Back to Integrations
          </Link>
        </EmptyState>
      </Page>
    )
  }

  const hasMappings = session.mappings.length > 0
  const selectMenuPortalStyles = {
    menuPortal: (base: Record<string, unknown>) => ({
      ...base,
      zIndex: 9999,
    }),
  }

  return (
    <Page>
      <BackLink to={PATH_STRINGS.userProfileIntegrations}>
        ← Integrations
      </BackLink>

      <Header>
        <Title>Schedule Import</Title>
      </Header>
      <Meta>
        <span>
          File: <strong>{session.fileName}</strong>
        </span>
        <span>
          Tasks: <strong>{session.schedule.tasks.length}</strong>
        </span>
        <span>
          Mapped: <strong>{session.mappings.length}</strong>
        </span>
      </Meta>

      {projectPublicId && updateSummary && !loadingMappings ? (
        <UpdateSummary>
          <UpdateSummaryTitle>Update summary</UpdateSummaryTitle>
          <UpdateSummaryList>
            <li>
              Carried from saved mappings:{' '}
              <strong>{updateSummary.recalledCount}</strong>
            </li>
            <li>
              With date/status/name changes vs saved:{' '}
              <strong>{updateSummary.changedTasks.length}</strong>
              {updateSummary.changedTasks.length > 0 ? (
                <UpdateSummaryDetail>
                  {updateSummary.changedTasks
                    .slice(0, 5)
                    .map(task => `${task.taskCode} — ${task.taskName}`)
                    .join('; ')}
                  {updateSummary.changedTasks.length > 5
                    ? ` (+${updateSummary.changedTasks.length - 5} more)`
                    : ''}
                </UpdateSummaryDetail>
              ) : null}
            </li>
            <li>
              Unmapped in this file:{' '}
              <strong>{updateSummary.unmappedInFileCount}</strong>
              {updateSummary.newInFileCount > 0
                ? ` (${updateSummary.newInFileCount} new vs last save)`
                : ''}
            </li>
            <li>
              Saved mappings not in this file (kept in project):{' '}
              <strong>{updateSummary.softKeptOrphans.length}</strong>
              {updateSummary.softKeptOrphans.length > 0 ? (
                <UpdateSummaryDetail>
                  {updateSummary.softKeptOrphans
                    .slice(0, 5)
                    .map(task => `${task.taskCode} — ${task.taskName}`)
                    .join('; ')}
                  {updateSummary.softKeptOrphans.length > 5
                    ? ` (+${updateSummary.softKeptOrphans.length - 5} more)`
                    : ''}
                  . Remove manually later if needed.
                </UpdateSummaryDetail>
              ) : null}
            </li>
          </UpdateSummaryList>
        </UpdateSummary>
      ) : null}

      <ControlsRow>
        <SelectGroup>
          <Field>
            <Label>Project</Label>
            <Select
              placeholder={
                projectsLoading ? 'Loading projects…' : 'Select a project…'
              }
              isClearable
              isLoading={projectsLoading}
              options={projectOptions}
              value={selectedProjectOption}
              onChange={onProjectChange}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                container: base => ({ ...base, minWidth: 180, width: 200 }),
                ...selectMenuPortalStyles,
              }}
            />
          </Field>
          <Field>
            <Label>Floor</Label>
            <Select
              placeholder={
                !projectPublicId
                  ? 'Select a project first'
                  : floorsLoading
                  ? 'Loading floors…'
                  : floorOptions.length === 0
                  ? 'No floors'
                  : 'Select floor…'
              }
              isDisabled={
                !projectPublicId || floorsLoading || floorOptions.length === 0
              }
              isLoading={floorsLoading}
              options={floorOptions}
              value={selectedFloorOption}
              onChange={(opt: FloorOption | null) =>
                setAssignFloorCode(opt?.value ?? null)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                container: base => ({ ...base, minWidth: 140, width: 170 }),
                ...selectMenuPortalStyles,
              }}
            />
          </Field>
          <Field>
            <Label>Tracker</Label>
            <Select
              placeholder={
                !projectPublicId
                  ? 'Select a project first'
                  : trackersLoading
                  ? 'Loading trackers…'
                  : 'Select tracker…'
              }
              isDisabled={!projectPublicId || trackersLoading}
              isLoading={trackersLoading}
              options={trackerOptions}
              value={selectedTrackerOption}
              onChange={(opt: TrackerOption | null) =>
                setAssignTrackerId(opt?.value ?? null)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                container: base => ({ ...base, minWidth: 140, width: 170 }),
                ...selectMenuPortalStyles,
              }}
            />
          </Field>
          <Field>
            <Label>Job</Label>
            <Select
              placeholder={
                !assignTrackerId
                  ? 'Select a tracker first'
                  : jobTypesLoading
                  ? 'Loading jobs…'
                  : jobTypeOptions.length === 0
                  ? 'No jobs for tracker'
                  : 'Select job…'
              }
              isDisabled={
                !assignTrackerId ||
                jobTypesLoading ||
                jobTypeOptions.length === 0
              }
              isLoading={jobTypesLoading}
              options={jobTypeOptions}
              value={selectedJobTypeOption}
              onChange={(opt: JobTypeOption | null) =>
                setAssignJobTypeId(opt?.value ?? null)
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                container: base => ({ ...base, minWidth: 140, width: 170 }),
                ...selectMenuPortalStyles,
              }}
            />
          </Field>
        </SelectGroup>

        <ButtonGroup>
          <AddProjectButton
            text="Assign"
            onClick={assignSelected}
            buttonStyle={{
              minWidth: 100,
              opacity:
                loadingMappings ||
                !assignFloorCode ||
                !assignTrackerId ||
                !assignJobTypeId ||
                selectedTaskIds.size === 0
                  ? 0.5
                  : 1,
              pointerEvents:
                loadingMappings ||
                !assignFloorCode ||
                !assignTrackerId ||
                !assignJobTypeId ||
                selectedTaskIds.size === 0
                  ? 'none'
                  : 'auto',
            }}
          />
          <AddProjectButton
            text="Clear All"
            onClick={clearAllMappings}
            buttonStyle={{
              minWidth: 100,
              opacity: hasMappings ? 1 : 0.5,
              pointerEvents: hasMappings ? 'auto' : 'none',
            }}
          />
          <AddProjectButton
            text={saving ? 'Saving…' : 'Save All'}
            onClick={() => {
              if (!saving) {
                void onSaveAll()
              }
            }}
            buttonStyle={{
              minWidth: 100,
              background: '#073c7a',
              color: '#ffffff',
              border: '1px solid #073c7a',
              opacity: saving || !projectPublicId ? 0.6 : 1,
              pointerEvents: saving || !projectPublicId ? 'none' : 'auto',
            }}
          />
        </ButtonGroup>
      </ControlsRow>

      <TableFilters>
        <Field grow>
          <Label>
            Search tasks
            {loadingMappings ? ' (loading saved mappings…)' : ''}
          </Label>
          <SearchInput
            type="search"
            placeholder="Filter by code, name, or WBS…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Field>
        <CheckboxField>
          <input
            id="unmapped-only"
            type="checkbox"
            checked={unmappedOnly}
            onChange={e => setUnmappedOnly(e.target.checked)}
          />
          <label htmlFor="unmapped-only">Unmapped only</label>
        </CheckboxField>
      </TableFilters>

      <Content>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAllFiltered}
                    aria-label="Select all filtered tasks"
                  />
                </Th>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>WBS</Th>
                <Th>Target start</Th>
                <Th>Target end</Th>
                <Th>Actual start</Th>
                <Th>Actual end</Th>
                <Th>Mapped to</Th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task: XerTask) => {
                const mapping = mappingsByTaskId.get(task.taskId)
                const typeLabel = taskTypeLabel(task.taskType)
                const statusText = statusLabel(task.statusCode)
                const targetStart = formatXerDate(task.dates.target_start_date)
                const targetEnd = formatXerDate(task.dates.target_end_date)
                const actStart = formatXerDate(task.dates.act_start_date)
                const actEnd = formatXerDate(task.dates.act_end_date)
                const mappedText = mapping ? mappingLabel(mapping) : '—'
                const wbsText = task.wbsPath || '—'
                return (
                  <tr key={task.taskId}>
                    <Td>
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task.taskId)}
                        onChange={() => toggleTask(task.taskId)}
                        aria-label={`Select ${task.taskCode}`}
                      />
                    </Td>
                    <Td mono title={task.taskCode}>
                      {task.taskCode}
                    </Td>
                    <Td title={task.taskName}>{task.taskName}</Td>
                    <Td title={typeLabel}>{typeLabel}</Td>
                    <Td title={statusText}>{statusText}</Td>
                    <Td muted title={wbsText}>
                      {wbsText}
                    </Td>
                    <Td mono title={targetStart}>
                      {targetStart}
                    </Td>
                    <Td mono title={targetEnd}>
                      {targetEnd}
                    </Td>
                    <Td mono title={actStart}>
                      {actStart}
                    </Td>
                    <Td mono title={actEnd}>
                      {actEnd}
                    </Td>
                    <Td title={mappedText}>
                      {mapping ? (
                        <MappedBadge>{mappedText}</MappedBadge>
                      ) : (
                        '—'
                      )}
                    </Td>
                  </tr>
                )
              })}
              {filteredTasks.length === 0 ? (
                <tr>
                  <Td colSpan={11} style={{ textAlign: 'center', padding: 24 }}>
                    No tasks match the current filters.
                  </Td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </TableWrap>

        <SummaryPanel>
          <SummaryTitle>Mappings summary</SummaryTitle>
          {!projectPublicId ? (
            <SummaryEmpty>Select a project to map trackers.</SummaryEmpty>
          ) : mappingsByTrackerJob.length === 0 ? (
            <SummaryEmpty>
              No mappings yet. Select schedule rows and assign them to a
              floor, tracker, and job type.
            </SummaryEmpty>
          ) : (
            mappingsByTrackerJob.map(group => (
              <SummaryGroup
                key={`${group.floorCode}:${group.trackerId}:${group.jobTypeId}`}
              >
                <SummaryGroupTitle>
                  {group.label} <Count>({group.tasks.length})</Count>
                </SummaryGroupTitle>
                <SummaryList>
                  {group.tasks.map(m => (
                    <SummaryItem key={m.taskId}>
                      <code>{m.taskCode}</code>
                      <RemoveMappingButton
                        type="button"
                        aria-label={`Remove mapping for ${m.taskCode}`}
                        onClick={() => removeMapping(m.taskId)}
                      >
                        ×
                      </RemoveMappingButton>
                    </SummaryItem>
                  ))}
                </SummaryList>
              </SummaryGroup>
            ))
          )}
        </SummaryPanel>
      </Content>
    </Page>
  )
}

const Page = styled.div`
  padding: 16px 20px 40px;
  max-width: 100%;
`

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #4a4a4a;

  code {
    background: #f0f2f5;
    padding: 2px 6px;
    border-radius: 4px;
  }
`

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 24px;
  color: #073c7a;
  text-decoration: none;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`

const Header = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  color: #1a1a1a;
  line-height: 1.2;
`

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #555;
  margin-bottom: 12px;
`

const UpdateSummary = styled.div`
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #334155;
`

const UpdateSummaryTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: #1e293b;
`

const UpdateSummaryList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const UpdateSummaryDetail = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: #f5f7fa;
  border: 1px solid #e6e8ec;
  border-radius: 6px;
  margin-bottom: 16px;
`

const SelectGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
`

const TableFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 10px;
`

const Field = styled.div<{ grow?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: ${props => (props.grow ? '1 1 220px' : '0 0 auto')};
  min-width: ${props => (props.grow ? '200px' : 'auto')};
`

const Label = styled.label`
  font-size: 12px;
  color: #4a4a4a;
`

const SearchInput = styled.input`
  height: 38px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
`

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  font-size: 14px;
  color: #333;
`

const Content = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 16px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e6e8ec;
  border-radius: 6px;
  max-height: calc(100vh - 320px);
  background: #fff;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  position: sticky;
  top: 0;
  background: #f8f9fb;
  text-align: left;
  padding: 10px 8px;
  border-bottom: 1px solid #e6e8ec;
  white-space: nowrap;
  font-weight: 600;
  color: #333;
  z-index: 1;
`

const Td = styled.td<{ mono?: boolean; muted?: boolean }>`
  padding: 8px;
  border-bottom: 1px solid #f0f2f5;
  vertical-align: top;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${props =>
    props.mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit'};
  color: ${props => (props.muted ? '#666' : 'inherit')};
`

const MappedBadge = styled.span`
  display: inline-block;
  background: #e8f1fb;
  color: #073c7a;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
`

const SummaryPanel = styled.aside`
  border: 1px solid #e6e8ec;
  border-radius: 6px;
  background: #fff;
  padding: 14px;
  max-height: calc(100vh - 320px);
  overflow: auto;
`

const SummaryTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 15px;
`

const SummaryEmpty = styled.p`
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`

const SummaryGroup = styled.div`
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`

const SummaryGroupTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: #1a1a1a;
`

const Count = styled.span`
  color: #666;
  font-weight: 500;
`

const SummaryList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style: none;
  font-size: 12px;
  color: #444;
  max-height: 160px;
  overflow: auto;

  code {
    font-size: 11px;
  }
`

const SummaryItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0;
`

const RemoveMappingButton = styled.button`
  border: none;
  background: transparent;
  color: #888;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 4px;
  border-radius: 3px;

  &:hover {
    color: #b00020;
    background: #f5e8ea;
  }
`
